package com.nonopn.skywatcher.common.login.sub

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import com.nonopn.skywatcher.common.ILoginCaller
import com.nonopn.skywatcher.common.login.LocalOpenSessionModel
import com.nonopn.skywatcher.common.login.pages.RouterMain
import com.nonopn.skywatcher.design.local.LocalConfirmPopup
import de.drick.compose.hotpreview.HotPreview
import eu.codlab.blipya.common_sub_login.generated.resources.Res
import eu.codlab.blipya.common_sub_login.generated.resources.app_login_sub_title
import eu.codlab.blipya.common_sub_login.generated.resources.cancel
import eu.codlab.blipya.common_sub_login.generated.resources.form_email
import eu.codlab.blipya.common_sub_login.generated.resources.form_password
import eu.codlab.blipya.common_sub_login.generated.resources.login_account_title
import eu.codlab.blipya.common_sub_login.generated.resources.unexpected_error
import eu.codlab.compose.widgets.CustomOutlinedButton
import eu.codlab.compose.widgets.CustomOutlinedEditText
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.compose.widgets.TextSubtitle
import eu.codlab.compose.widgets.TextTitle
import eu.codlab.compose.widgets.preview.PreviewDarkLightColumn
import kotlinx.serialization.Serializable
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

@Suppress("LongMethod", "UnusedPrivateMember")
@Composable
fun LogInAccountScreen(
    loginCaller: ILoginCaller,
    loginTitle: StringResource,
    modifier: Modifier = Modifier
) {
    val model = LocalOpenSessionModel.current

    var loginState by remember { mutableStateOf(LoginState()) }
    var username by remember { mutableStateOf(TextFieldValue("")) }
    var password by remember { mutableStateOf(TextFieldValue("")) }

    val scrollState = rememberScrollState()

    Column(
        modifier = modifier
            .padding(16.dp)
            .fillMaxHeight()
    ) {
        val popup = LocalConfirmPopup.current
        val errorPopupTitle = stringResource(Res.string.unexpected_error)

        TextTitle(
            text = stringResource(loginTitle)
        )
        TextSubtitle(
            text = stringResource(Res.string.app_login_sub_title)
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .verticalScroll(scrollState)
        ) {
            CustomOutlinedEditText(
                value = username,
                onValueChanged = {
                    loginState = loginState.newState(username = it.text)
                    username = it
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                singleLine = true,
                isError = !loginState.valid,
                label = {
                    TextNormal(
                        text = stringResource(Res.string.form_email)
                    )
                }
            )

            CustomOutlinedEditText(
                value = password,
                onValueChanged = {
                    loginState = loginState.newState(password = it.text)

                    password = it
                },
                label = {
                    TextNormal(
                        text = stringResource(Res.string.form_password)
                    )
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                singleLine = true,
                isError = !loginState.valid,
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                Column(
                    Modifier
                        .background(Color.Transparent)
                        .width(IntrinsicSize.Max),
                    Arrangement.spacedBy(5.dp)
                ) {
                    CustomOutlinedButton(
                        modifier = Modifier.fillMaxWidth(),
                        onClick = {
                            loginCaller.login(username.text, password.text) { err ->
                                if (null == err) return@login

                                popup.show(
                                    title = errorPopupTitle,
                                    text = err.message ?: errorPopupTitle
                                )
                            }
                        },
                        content = { TextNormal(stringResource(Res.string.login_account_title)) }
                    )
                }
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                CustomOutlinedButton(
                    onClick = { model.show(RouterMain.navigateTo()) },
                    content = {
                        TextNormal(
                            stringResource(Res.string.cancel)
                        )
                    }
                )
            }
        }
    }
}

@Serializable
private data class LoginState(
    val username: String = "",
    val password: String = "",
    var valid: Boolean = false
) {
    fun newState(username: String? = null, password: String? = null): LoginState {
        val newUsername = username ?: this.username
        val newPassword = password ?: this.password

        return copy(
            username = newUsername,
            password = newPassword,
            valid = newUsername.isNotBlank() && newPassword.isNotBlank()
        )
    }
}

@HotPreview(widthDp = 500, heightDp = 360, darkMode = true)
@HotPreview(widthDp = 500, heightDp = 360, darkMode = false)
@HotPreview(widthDp = 360, heightDp = 500, darkMode = true)
@HotPreview(widthDp = 360, heightDp = 500, darkMode = false)
@Composable
private fun LogInAccountScreenDisplayPreview() {
    PreviewDarkLightColumn(submodifier = { Modifier.height(200.dp) }) { modifier, _ ->
        LogInAccountScreen(
            // appModel = AppModel.fake(),
            loginCaller = object : ILoginCaller {
                override fun register(
                    email: String,
                    password: String,
                    callback: (err: Throwable?) -> Unit
                ) = Unit

                override fun login(
                    email: String,
                    password: String,
                    callback: (err: Throwable?) -> Unit
                ) = Unit
            },
            loginTitle = Res.string.login_account_title,
            modifier = modifier
        )
    }
}
